/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/AiAssistantSources.tsx
 * PURPOSE: Perplexity-style sources — structured rows from Langflow + URL chips from markdown.
 */

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

import type { AssistantStructuredSource } from './assistantContentPartition';
import { shortHost } from './extractSourceUrls';

export interface AiAssistantSourcesProps {
  /** From `<!-- brazn-sources ... -->` in the assistant message (preferred). */
  structured?: AssistantStructuredSource[];
  /** Extra http(s) links found in the visible markdown (deduped against structured). */
  extraUrls?: string[];
}

export function AiAssistantSources({ structured = [], extraUrls = [] }: AiAssistantSourcesProps) {
  if (!structured.length && !extraUrls.length) return null;

  return (
    <div className="mt-3 space-y-2" aria-label="Sources">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Sources</p>

      {structured.length > 0 && (
        <ul className="space-y-2">
          {structured.map((s) => (
            <li key={s.url}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg border border-gray-200 bg-white px-3 py-2 text-left shadow-sm transition hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-600 dark:bg-gray-800/90 dark:hover:border-primary-500 dark:hover:bg-primary-950/30"
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-[13px] font-semibold text-gray-900 dark:text-gray-50">
                      {s.title?.trim() || shortHost(s.url)}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] font-medium text-primary-700 dark:text-primary-300">
                      {shortHost(s.url)}
                    </span>
                    {s.snippet?.trim() ? (
                      <span className="mt-1 line-clamp-2 text-[11px] leading-snug text-gray-600 dark:text-gray-400">
                        {s.snippet.trim()}
                      </span>
                    ) : null}
                  </span>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {extraUrls.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {extraUrls.map((href) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-primary-700 shadow-sm hover:border-primary-300 hover:bg-primary-50/80 dark:border-gray-600 dark:bg-gray-800 dark:text-primary-300 dark:hover:border-primary-500 dark:hover:bg-primary-950/40"
            >
              <span className="truncate">{shortHost(href)}</span>
              <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
